import { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { useTheme } from "../context/ThemeContext";
import { Droplets, Wind, ChevronLeft, ChevronRight } from "lucide-react";
import WeatherIcon from "./WeatherIcon";
import { Button } from "./ui/button";

export const HourlyChart = ({ data, tempUnit = "F" }) => {
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const getTemp = (temp) => {
    if (tempUnit === "C") {
      return Math.round((temp - 32) * 5 / 9);
    }
    return Math.round(temp);
  };

  const getRainColor = (pop) => {
    if (pop >= 70) return isDark ? "text-blue-400" : "text-blue-600";
    if (pop >= 40) return isDark ? "text-cyan-400" : "text-cyan-600";
    if (pop >= 20) return isDark ? "text-sky-400" : "text-sky-500";
    return isDark ? "text-white/40" : "text-gray-400";
  };

  const getRainBgColor = (pop) => {
    if (pop >= 70) return isDark ? "bg-blue-500/20" : "bg-blue-100";
    if (pop >= 40) return isDark ? "bg-cyan-500/15" : "bg-cyan-50";
    if (pop >= 20) return isDark ? "bg-sky-500/10" : "bg-sky-50";
    return "";
  };

  const checkScroll = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  useEffect(() => {
    checkScroll();
    window.addEventListener('resize', checkScroll);
    return () => window.removeEventListener('resize', checkScroll);
  }, []);

  const scroll = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = 200;
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
      setTimeout(checkScroll, 300);
    }
  };

  return (
    <div className="relative">
      {/* Navigation Buttons - Hidden on mobile, shown on desktop */}
      {canScrollLeft && (
        <Button
          variant="ghost"
          size="icon"
          onClick={() => scroll('left')}
          className="absolute left-0 top-1/2 -translate-y-1/2 z-10 h-8 w-8 rounded-full bg-black/60 backdrop-blur-sm hover:bg-black/80 border border-white/10 hidden md:flex"
        >
          <ChevronLeft className="w-4 h-4" />
        </Button>
      )}
      
      {canScrollRight && (
        <Button
          variant="ghost"
          size="icon"
          onClick={() => scroll('right')}
          className="absolute right-0 top-1/2 -translate-y-1/2 z-10 h-8 w-8 rounded-full bg-black/60 backdrop-blur-sm hover:bg-black/80 border border-white/10 hidden md:flex"
        >
          <ChevronRight className="w-4 h-4" />
        </Button>
      )}

      {/* Hourly Cards - Finger scrollable */}
      <div 
        ref={scrollRef}
        onScroll={checkScroll}
        className="flex gap-2 overflow-x-auto scrollbar-hide pb-2 px-1 snap-x snap-mandatory touch-pan-x"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}
      >
        {data.map((hour, idx) => {
          const time = new Date(hour.dt * 1000);
          const isNow = idx === 0;
          const rainChance = Math.round((hour.pop || 0) * 100);
          const dayName = time.toLocaleDateString("en-US", { weekday: "short" });
          
          return (
            <motion.div
              key={hour.dt}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.02 }}
              className={`flex-shrink-0 w-32 snap-start p-4 rounded-2xl text-center transition-all ${
                isNow 
                  ? isDark ? 'bg-[#00f2ea]/20 border border-[#00f2ea]/30' : 'bg-cyan-100 border border-cyan-300'
                  : isDark ? 'glass-light' : 'bg-black/5 hover:bg-black/10'
              } ${getRainBgColor(rainChance)}`}
            >
              {/* Day & Time */}
              <p className={`text-xs mb-1 ${isNow ? (isDark ? 'text-[#00f2ea]/70' : 'text-cyan-500') : 'text-white/40'}`}>
                {dayName}
              </p>
              <p className={`text-sm font-medium mb-2 ${isNow ? (isDark ? 'text-[#00f2ea]' : 'text-cyan-600') : 'text-white/70'}`}>
                {isNow ? 'Now' : time.toLocaleTimeString("en-US", { 
                  hour: "numeric",
                  hour12: true 
                })}
              </p>
              
              {/* Weather Icon */}
              <div className="flex justify-center mb-2">
                <WeatherIcon 
                  code={hour.weather?.[0]?.id || 800} 
                  size={40}
                  isDay={time.getHours() >= 6 && time.getHours() < 20}
                />
              </div>
              
              {/* Temperature */}
              <div className="space-y-1 text-center">
                <p className="text-xl font-bold">
                  {getTemp(hour.temp)}°
                </p>
                {hour.feels_like && (
                  <p className="text-sm text-white/40">
                    Feels {getTemp(hour.feels_like)}°
                  </p>
                )}
              </div>
              
              {/* Additional Details */}
              <div className="mt-3 pt-3 border-t border-white/10 space-y-1 text-xs text-white/60">
                <div className="flex justify-between">
                  <span>Rain</span>
                  <span className={getRainColor(rainChance)}>{rainChance}%</span>
                </div>
                {hour.humidity && (
                  <div className="flex justify-between">
                    <span>Humidity</span>
                    <span>{hour.humidity}%</span>
                  </div>
                )}
                {hour.wind_speed > 0 && (
                  <div className="flex justify-between">
                    <span>Wind</span>
                    <span>{Math.round(hour.wind_speed)} mph</span>
                  </div>
                )}
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
};

export default HourlyChart;
